import React from "react";
import { object, shape, string, oneOf, any } from "prop-types";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale } from "@insightfulscience/i18";
import { BackToTopButton } from "@insightfulscience/atomic-react/r2/BackToTopButton";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import { PostElementParagraph } from "@insightfulscience/atomic-react/PostElement";
import GuidePageTemplate from "@insightfulscience/atomic-react/GuidePageTemplate";
import GuidePageTemplateMain from "@insightfulscience/atomic-react/GuidePageTemplate/Main";
import GuidePageTemplateSidebar from "@insightfulscience/atomic-react/GuidePageTemplate/Sidebar";
import {
	GuidePageTemplateNavItem,
	GuidePageTemplateNavItemLink,
} from "@insightfulscience/atomic-react/GuidePageTemplate/Nav";
import { scrollToHeading } from "@insightfulscience/atomic-react/utils/Guides";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import NextLink from "@insightfulscience/smart-react/NextLink";
import {
	generateHeadingId,
	sidebarNavigation,
} from "@insightfulscience/contentful/richTextTypes/default/utils";
import richTextTypes from "@insightfulscience/contentful/richTextTypes/default";
import { PUBLISHED } from "@insightfulscience/contentful/constants";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import { Col } from "@insightfulscience/atomic-react/Grid";
import ButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import Icon from "@insightfulscience/atomic-react/Icon";
import Section from "@insightfulscience/atomic-react/Section";

const ReleaseNotesPage = ({ post, pageProps }) => {
	const [t] = useLocale("releaseNotes");
	const { siteDomain } = pageProps;
	const {
		title,
		metaDescription = "",
		thumbnail,
		slug,
		releaseDate,
		publishStatus,
		body,
		windowsInstallerUrl = "",
		linuxInstallerUrl = "",
		macInstallerUrl = "",
		appVersionNumber = "",
	} = post;

	const thumbnailUrl = thumbnail?.fields?.file?.url || "";
	const currentPageURL = `${siteDomain}/updates/${slug}`;
	const navigation = body ? sidebarNavigation(body) : [];
	const metaRobotsContent = publishStatus === "Live" ? "index, follow" : "noindex, follow";
	const wasReleasedMessage = (
		<PostElementParagraph italic suppressHydrationWarning>
			{appVersionNumber} {t("UPDATES.WAS_RELEASED_ON")}{" "}
			{t("{{releaseDate, LONGDATE}}", { releaseDate })}.
		</PostElementParagraph>
	);

	const totalHeading2 = body.content.reduce((total, current) => {
		return total + (current.nodeType === "heading-2" ? 1 : 0);
	}, 0);
	const isSidebar = totalHeading2 > 0;

	return (
		<>
			<PageMeta title={title} description={metaDescription}>
				<PageMeta.OGP
					title={title || t("META_TITLE")}
					description={metaDescription || t("META_DESCRIPTION")}
					url={`${siteDomain}/updates/${slug}`}
					image={thumbnailUrl || t("META_IMAGE")}
					imageSecureUrl={thumbnailUrl || t("META_IMAGE_SECURE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Twitter
					card={t("META_TWITTER_CARD")}
					site={t("META_TWITTER_SITE")}
					creator={t("META_TWITTER_CREATOR")}
					title={title || t("META_TITLE")}
					description={metaDescription || t("META_DESCRIPTION")}
					image={thumbnailUrl || t("META_TWITTER_IMAGE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.OGP
					title={title || t("META_TITLE")}
					description={metaDescription || t("META_DESCRIPTION")}
					url={`${siteDomain}/updates/${slug}`}
					image={thumbnailUrl || t("META_IMAGE")}
					imageSecureUrl={thumbnailUrl || t("META_IMAGE_SECURE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Twitter
					card={t("META_TWITTER_CARD")}
					site={t("META_TWITTER_SITE")}
					creator={t("META_TWITTER_CREATOR")}
					title={title || t("META_TITLE")}
					description={metaDescription || t("META_DESCRIPTION")}
					image={thumbnailUrl || t("META_TWITTER_IMAGE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Canonical href={currentPageURL} />
				<PageMeta.Robots content={metaRobotsContent} />
			</PageMeta>
			<Hero color="4">
				<HeroRow spacing={{ mb: 6 }}>
					<HeroCol lg={12} sx={{ textAlign: "left" }}>
						<NextLink href="/updates">
							<Icon name="angle-left" /> Back to Updates
						</NextLink>
					</HeroCol>
				</HeroRow>
				<HeroTitle weight="medium" color="black">
					{title}
				</HeroTitle>
				<HeroRow spacing={{ mt: 5 }}>
					{windowsInstallerUrl && (
						<Col lg="shrink">
							<ButtonLink href={windowsInstallerUrl} styleType="primary">
								<Icon name="windows" /> Download for Windows
							</ButtonLink>
						</Col>
					)}
					{macInstallerUrl && (
						<Col lg="shrink">
							<ButtonLink href={macInstallerUrl} styleType="primary">
								<Icon name="mac" /> Download for macOS
							</ButtonLink>
						</Col>
					)}
					{linuxInstallerUrl && (
						<Col lg="shrink">
							<ButtonLink href={linuxInstallerUrl} styleType="primary">
								<Icon name="linux" /> Download for Linux
							</ButtonLink>
						</Col>
					)}
				</HeroRow>
			</Hero>

			<Section spacing={{ sm: { mt: 10 } }}>
				<GuidePageTemplate sidebar={isSidebar}>
					{navigation ? (
						<GuidePageTemplateSidebar>
							{navigation.map(item => {
								return (
									<GuidePageTemplateNavItem key={item}>
										<GuidePageTemplateNavItemLink
											href={`#${generateHeadingId(item)}`}
											onClick={e => scrollToHeading(e)}
										>
											{item}
										</GuidePageTemplateNavItemLink>
									</GuidePageTemplateNavItem>
								);
							})}
						</GuidePageTemplateSidebar>
					) : null}
					<GuidePageTemplateMain sidebar={isSidebar}>
						{publishStatus === "Live" && wasReleasedMessage}
						{body ? documentToReactComponents(body, richTextTypes) : null}
					</GuidePageTemplateMain>
				</GuidePageTemplate>
			</Section>
			<BackToTopButton />
		</>
	);
};

ReleaseNotesPage.propTypes = {
	post: shape({
		title: string.isRequired,
		metaDescription: string,
		appReleaseNotes: object, // eslint-disable-line react/forbid-prop-types
		appVersionNumber: string,
		body: object, // eslint-disable-line react/forbid-prop-types
		publishStatus: oneOf(["Hidden", PUBLISHED]),
		releaseDate: string,
		slug: string,
		teaser: object, // eslint-disable-line react/forbid-prop-types
		macInstallerUrl: string,
		windowsInstallerUrl: string,
		linuxInstallerUrl: string,
	}),
	pageProps: shape({
		resources: any, // eslint-disable-line react/forbid-prop-types,
		siteDomain: string,
	}),
};

export default ReleaseNotesPage;
